import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestTube, Save, MessageSquare, Mail, ShieldCheck, UserCog, Lock, ClipboardCheck, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import TwoFactorSettings from "./TwoFactorSettings";
import DataImport from "./DataImport";
import PrescriptionCardSettings from "./PrescriptionCardSettings";

const ABDM_ENVS = [
  { v: "sandbox", label: "Sandbox (development)" },
  { v: "production", label: "Production (live)" },
];

export default function AdminSettings() {
  const [sms, setSms] = useState({ provider: "msg91", sender_id: "", api_key: "", dlt_entity_id: "", enabled: false });
  const [smtp, setSmtp] = useState({ host: "smtp.gmail.com", port: 587, username: "", password: "", sender_name: "", sender_email: "", enabled: false });
  const [abdm, setAbdm] = useState({ environment: "sandbox", base_url: "https://dev.abdm.gov.in", client_id: "", client_secret: "", hfr_id: "", hfr_name: "", facility_type: "Hospital", bridge_id: "", enabled: false });
  const [doctors, setDoctors] = useState([]);
  // Registration-workflow settings — controls what auto-prints when a
  // receptionist completes patient registration. Sticky per hospital.
  const [workflow, setWorkflow] = useState({ rx_online: true, print_sticker: true, print_prescription_card: false });
  const [testMobile, setTestMobile] = useState("");
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    // All four endpoints are tenant-scoped and return 400 for super_admin
    // (no hospital_id on the token). Catch silently — the page still renders,
    // and each Save button will only work when the user has a real hospital.
    api.get("/settings/sms").then(({ data }) => setSms(s => ({ ...s, ...data }))).catch(() => {});
    api.get("/settings/smtp").then(({ data }) => setSmtp(s => ({ ...s, ...data }))).catch(() => {});
    api.get("/settings/abdm").then(({ data }) => setAbdm(s => ({ ...s, ...data }))).catch(() => {});
    api.get("/hospital/doctors").then(({ data }) => setDoctors(data.items || [])).catch(() => setDoctors([]));
    api.get("/hospital/settings/registration-workflow").then(({ data }) => setWorkflow(w => ({ ...w, ...data }))).catch(() => {});
  }, []);

  const saveWorkflow = async () => {
    try {
      await api.patch("/hospital/settings/registration-workflow", workflow);
      toast.success("Registration workflow saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const saveSms = async () => { try { await api.post("/settings/sms", sms); toast.success("SMS settings saved"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const saveSmtp = async () => { try { await api.post("/settings/smtp", smtp); toast.success("SMTP settings saved"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const saveAbdm = async () => { try { await api.post("/settings/abdm", abdm); toast.success("ABDM settings saved"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const testSms = async () => { const { data } = await api.post("/settings/sms/test", { mobile: testMobile }); toast.info(data.message); };
  const testSmtp = async () => { const { data } = await api.post("/settings/smtp/test", { email: testEmail }); toast.info(data.message); };
  const testAbdm = async () => { const { data } = await api.post("/settings/abdm/test"); toast.info(data.message); };
  const saveHpr = async (doc, hpr_id, hpr_address) => {
    try { await api.post("/settings/abdm/hpr", { user_id: doc.id, hpr_id, hpr_address }); toast.success(`HPR saved for ${doc.name}`); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="section-label">Administration</div>
        <h1 className="font-heading text-3xl font-semibold">Hospital settings</h1>
        <p className="mt-1 text-sm text-ink-muted">Configure SMS, email, ABDM and integrations. Values are tenant-scoped (per hospital).</p>
      </div>

      <Tabs defaultValue="sms">
        <TabsList>
          <TabsTrigger value="sms" data-testid="settings-tab-sms"><MessageSquare size={14} className="mr-2" />SMS</TabsTrigger>
          <TabsTrigger value="smtp" data-testid="settings-tab-smtp"><Mail size={14} className="mr-2" />Email (SMTP)</TabsTrigger>
          <TabsTrigger value="abdm" data-testid="settings-tab-abdm"><ShieldCheck size={14} className="mr-2" />ABDM</TabsTrigger>
          <TabsTrigger value="hpr" data-testid="settings-tab-hpr"><UserCog size={14} className="mr-2" />HPR mapping</TabsTrigger>
          <TabsTrigger value="2fa" data-testid="settings-tab-2fa"><Lock size={14} className="mr-2" />Two-factor auth</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="settings-tab-workflow"><ClipboardCheck size={14} className="mr-2" />Registration workflow</TabsTrigger>
          <TabsTrigger value="rxcard" data-testid="settings-tab-rxcard"><FileText size={14} className="mr-2" />Prescription card</TabsTrigger>
          <TabsTrigger value="import" data-testid="settings-tab-import"><Upload size={14} className="mr-2" />Data import</TabsTrigger>
        </TabsList>

        <TabsContent value="sms" className="mt-4">
          <Card className="p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Provider</Label>
                <Select value={sms.provider} onValueChange={(v) => setSms({ ...sms, provider: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["msg91","textlocal","twilio","custom"].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Sender ID</Label><Input value={sms.sender_id} onChange={e => setSms({ ...sms, sender_id: e.target.value })} placeholder="DLT-approved sender ID" /></div>
              <div><Label>API Key</Label><Input type="password" value={sms.api_key} onChange={e => setSms({ ...sms, api_key: e.target.value })} /></div>
              <div><Label>DLT Entity ID</Label><Input value={sms.dlt_entity_id} onChange={e => setSms({ ...sms, dlt_entity_id: e.target.value })} /></div>
              <div className="flex items-center gap-3"><Switch checked={!!sms.enabled} onCheckedChange={(v) => setSms({ ...sms, enabled: v })} /><Label>Enable SMS service</Label></div>
            </div>
            <div className="mt-5 flex items-center gap-2">
              <Button onClick={saveSms} className="bg-brand hover:bg-brand-700" data-testid="sms-save-btn"><Save size={14} className="mr-2" />Save</Button>
              <Input className="max-w-xs" placeholder="+91 98xxxxxxxx" value={testMobile} onChange={e => setTestMobile(e.target.value)} />
              <Button variant="outline" onClick={testSms} data-testid="sms-test-btn"><TestTube size={14} className="mr-2" />Send test SMS</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="smtp" className="mt-4">
          <Card className="p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div><Label>SMTP host</Label><Input value={smtp.host} onChange={e => setSmtp({ ...smtp, host: e.target.value })} /></div>
              <div><Label>Port</Label><Input type="number" value={smtp.port} onChange={e => setSmtp({ ...smtp, port: Number(e.target.value) })} /></div>
              <div><Label>Username</Label><Input value={smtp.username} onChange={e => setSmtp({ ...smtp, username: e.target.value })} placeholder="you@gmail.com" /></div>
              <div><Label>App password</Label><Input type="password" value={smtp.password} onChange={e => setSmtp({ ...smtp, password: e.target.value })} placeholder="Gmail app password" /></div>
              <div><Label>Sender name</Label><Input value={smtp.sender_name} onChange={e => setSmtp({ ...smtp, sender_name: e.target.value })} /></div>
              <div><Label>Sender email</Label><Input value={smtp.sender_email} onChange={e => setSmtp({ ...smtp, sender_email: e.target.value })} /></div>
              <div className="flex items-center gap-3"><Switch checked={!!smtp.enabled} onCheckedChange={(v) => setSmtp({ ...smtp, enabled: v })} /><Label>Enable email service</Label></div>
            </div>
            <div className="mt-5 flex items-center gap-2">
              <Button onClick={saveSmtp} className="bg-brand hover:bg-brand-700" data-testid="smtp-save-btn"><Save size={14} className="mr-2" />Save</Button>
              <Input className="max-w-xs" placeholder="test@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} />
              <Button variant="outline" onClick={testSmtp} data-testid="smtp-test-btn"><TestTube size={14} className="mr-2" />Send test email</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="abdm" className="mt-4">
          <Card className="p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Environment</Label>
                <Select value={abdm.environment} onValueChange={(v) => setAbdm({ ...abdm, environment: v, base_url: v === "production" ? "https://abdm.gov.in" : "https://dev.abdm.gov.in" })}>
                  <SelectTrigger data-testid="abdm-env-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ABDM_ENVS.map(e => <SelectItem key={e.v} value={e.v}>{e.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Base URL</Label><Input value={abdm.base_url} onChange={e => setAbdm({ ...abdm, base_url: e.target.value })} /></div>
              <div><Label>Client ID</Label><Input value={abdm.client_id} onChange={e => setAbdm({ ...abdm, client_id: e.target.value })} data-testid="abdm-client-id" /></div>
              <div><Label>Client Secret</Label><Input type="password" value={abdm.client_secret} onChange={e => setAbdm({ ...abdm, client_secret: e.target.value })} data-testid="abdm-client-secret" /></div>
              <div><Label>HFR ID (facility)</Label><Input value={abdm.hfr_id} onChange={e => setAbdm({ ...abdm, hfr_id: e.target.value })} placeholder="e.g. IN3210000001" data-testid="abdm-hfr-id" /></div>
              <div><Label>HFR registered name</Label><Input value={abdm.hfr_name} onChange={e => setAbdm({ ...abdm, hfr_name: e.target.value })} /></div>
              <div>
                <Label>Facility type</Label>
                <Select value={abdm.facility_type} onValueChange={(v) => setAbdm({ ...abdm, facility_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Hospital","Clinic","Day Care","Diagnostic Lab","Pharmacy"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Bridge ID (HIP / HIU)</Label><Input value={abdm.bridge_id} onChange={e => setAbdm({ ...abdm, bridge_id: e.target.value })} /></div>
              <div className="flex items-center gap-3 md:col-span-2"><Switch checked={!!abdm.enabled} onCheckedChange={(v) => setAbdm({ ...abdm, enabled: v })} data-testid="abdm-enabled" /><Label>Enable live ABDM (M1–M4). When disabled the platform stays in stub/audit-only mode for this hospital.</Label></div>
            </div>
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Get your credentials from <b>https://sandbox.abdm.gov.in</b> (Bridge → Client management) and your <b>HFR ID</b> from <b>https://facility.abdm.gov.in</b>. Each branch/facility may have its own HFR ID — capture it here.
            </div>
            <div className="mt-5 flex items-center gap-2">
              <Button onClick={saveAbdm} className="bg-brand hover:bg-brand-700" data-testid="abdm-save-btn"><Save size={14} className="mr-2" />Save</Button>
              <Button variant="outline" onClick={testAbdm} data-testid="abdm-test-btn"><TestTube size={14} className="mr-2" />Test connection</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="hpr" className="mt-4">
          <Card className="overflow-hidden">
            <div className="border-b border-border p-3">
              <div className="font-heading text-base font-semibold">HPR (Healthcare Professional Registry) mapping</div>
              <div className="mt-1 text-xs text-ink-muted">Map each doctor to their HPR ID. Get HPR ID at <b>https://hpr.abdm.gov.in</b>. Required for ABDM M2/M4 document push.</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                <tr><th className="px-3 py-2 text-left">Doctor</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">HPR ID</th><th className="px-3 py-2 text-left">HPR address</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {doctors.length === 0 && <tr><td colSpan="5" className="px-3 py-10 text-center text-ink-muted">No doctors in this hospital yet.</td></tr>}
                {doctors.map(d => <HprRow key={d.id} doc={d} onSave={saveHpr} />)}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="2fa" className="mt-4">
          <TwoFactorSettings />
        </TabsContent>

        <TabsContent value="workflow" className="mt-4">
          <Card className="p-6" data-testid="settings-workflow-card">
            <div className="mb-4">
              <div className="section-label">Registration workflow</div>
              <div className="mt-1 text-[12px] text-ink-muted">
                Decide what happens after a receptionist completes patient registration.
                Any combination can be enabled — enabled options will auto-open in a new tab so the receptionist can print immediately.
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-secondary/40 p-3">
                <input
                  type="checkbox"
                  checked={workflow.rx_online}
                  onChange={(e) => setWorkflow({ ...workflow, rx_online: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-brand"
                  data-testid="workflow-rx-online"
                />
                <div className="text-sm">
                  <b>Prescription online (OPD console)</b>
                  <div className="text-[11px] text-ink-muted">Doctor writes the Rx digitally inside the OPD consultation screen. Default choice for paperless clinics.</div>
                </div>
              </label>

              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-secondary/40 p-3">
                <input
                  type="checkbox"
                  checked={workflow.print_sticker}
                  onChange={(e) => setWorkflow({ ...workflow, print_sticker: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-brand"
                  data-testid="workflow-print-sticker"
                />
                <div className="text-sm">
                  <b>Print OPD sticker</b>
                  <div className="text-[11px] text-ink-muted">Small 100×50 mm label with UHID, Name, Age/Sex, Date/Time, Mobile, Doctor. Great for label printers (Zebra/TSC).</div>
                </div>
              </label>

              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-secondary/40 p-3">
                <input
                  type="checkbox"
                  checked={workflow.print_prescription_card}
                  onChange={(e) => setWorkflow({ ...workflow, print_prescription_card: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-brand"
                  data-testid="workflow-print-prescription-card"
                />
                <div className="text-sm">
                  <b>Print prescription / OPD card</b>
                  <div className="text-[11px] text-ink-muted">A5 card with hospital header, patient identifiers (UHID/Name/Age/Sex/Mobile), doctor details, complaints/Rx blank area — for doctors who prefer paper.</div>
                </div>
              </label>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <Button onClick={saveWorkflow} className="bg-brand hover:bg-brand-700" data-testid="workflow-save-btn">
                <Save size={14} className="mr-2" />Save workflow
              </Button>
              <span className="text-[11px] text-ink-muted">Applies to all branches of this hospital.</span>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="import" className="mt-4">
          <DataImport />
        </TabsContent>

        <TabsContent value="rxcard" className="mt-4">
          <PrescriptionCardSettings />
        </TabsContent>

      </Tabs>
    </div>
  );
}

function HprRow({ doc, onSave }) {
  const [hprId, setHprId] = useState(doc.hpr_id || "");
  const [hprAddr, setHprAddr] = useState(doc.hpr_address || "");
  return (
    <tr>
      <td className="px-3 py-2 font-medium">{doc.name}</td>
      <td className="px-3 py-2 text-xs text-ink-muted">{doc.email}</td>
      <td className="px-3 py-2"><Input value={hprId} onChange={e => setHprId(e.target.value)} placeholder="e.g. 71-1234-5678-9012" /></td>
      <td className="px-3 py-2"><Input value={hprAddr} onChange={e => setHprAddr(e.target.value)} placeholder="dr.name@sbx" /></td>
      <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => onSave(doc, hprId, hprAddr)}>Save</Button></td>
    </tr>
  );
}
